/**
 * Submit-week needs-review count hook (task 204 D7-fe).
 *
 * Wraps `GET /api/v1/timesheets/:week/needs-review-count`. The count is
 * the number of pending-below-threshold suggestion rows in the week —
 * the submit-week confirmation modal fires when this is > 0.
 *
 * Also exports `useConfirmNeedsReviewAsInternal` — the mutation that the
 * "Submit as Internal" action triggers. The backend emits the
 * `classification.suggestion.accepted_at_submit` audit code for each
 * pending row.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import type { ConfirmAsInternalResponse, NeedsReviewCountResponse } from '@/types/suggestion';

interface UseNeedsReviewCountResult {
  count: number | null;
  isLoading: boolean;
}

// Mockup-mode count. 23 was the stub value 187 wired into the
// SubmitWeek confirmation modal copy; keep them in sync so fixture mode
// looks coherent end-to-end.
const MOCKUP_COUNT = 23;

export function useNeedsReviewCount(week: string | null): UseNeedsReviewCountResult {
  const { mockupMode } = useAppContext();
  const getToken = useToken();

  const query = useQuery({
    queryKey: ['needsReviewCount', week],
    queryFn: async () => {
      if (!week) return { count: 0 };
      const token = await getToken();
      return apiFetch<NeedsReviewCountResponse>(
        `/api/v1/timesheets/${encodeURIComponent(week)}/needs-review-count`,
        token,
      );
    },
    enabled: !mockupMode && !!week,
  });

  if (mockupMode) {
    return { count: week ? MOCKUP_COUNT : 0, isLoading: false };
  }
  return { count: query.data?.count ?? null, isLoading: query.isLoading };
}

export function useConfirmNeedsReviewAsInternal() {
  const { mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation<ConfirmAsInternalResponse, Error, { week: string }>({
    mutationFn: async ({ week }) => {
      if (mockupMode) {
        return { confirmedCount: MOCKUP_COUNT };
      }
      const token = await getToken();
      return apiFetch<ConfirmAsInternalResponse>(
        `/api/v1/timesheets/${encodeURIComponent(week)}/needs-review/confirm-as-internal`,
        token,
        { method: 'POST', body: JSON.stringify({}) },
      );
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['needsReviewCount', vars.week] });
    },
  });
}
